<template>
  <div class="docs">
    <h3 class="docs__title">Инструкции и регламенты</h3>
    <div class="docs__layout">
      <ul class="docs__list">
        <li v-for="doc of docs" :key="doc.id">
          <button
            :class="['docs__item', { 'docs__item--active': currentDoc && currentDoc.id === doc.id }]"
            @click="selectDoc(doc)"
          >
            <span :class="['docs__type', `docs__type--${doc.extension}`]">{{ doc.extension }}</span>
            <span class="docs__item-title">{{ doc.name }}</span>
            <el-tag v-if="!doc.is_viewed" class="docs__new" type="danger" size="mini" effect="dark">
              Новое
            </el-tag>
          </button>
        </li>
      </ul>

      <div class="docs__preview" v-if="!mobileMode">
        <div class="docs__empty" v-if="!currentDoc">
          <v-icon name="fileLines" :size="60" />
          <span v-if="loading">Загрузка...</span>
          <span v-else-if="!docs.length">Документов пока нет</span>
          <span v-else>Выберите документ из списка</span>
        </div>
        <template v-else>
          <div class="docs__preview-header">
            <span class="docs__preview-title">{{ currentDoc.name }}</span>
            <a
              v-if="isPdf(currentDoc)"
              class="docs__action"
              :href="currentDoc.url"
              target="_blank"
              rel="noopener"
            >
              <i class="el-icon-top-right"></i> Открыть в новой вкладке
            </a>
            <a
              class="docs__action"
              :href="currentDoc.url"
              :download="fileName(currentDoc)"
              target="_blank"
              rel="noopener"
              @click="markViewed(currentDoc)"
            >
              <i class="el-icon-download"></i> Скачать
            </a>
          </div>
          <iframe
            v-if="isPdf(currentDoc)"
            :key="currentDoc.id"
            class="docs__frame"
            :src="currentDoc.url"
            :title="currentDoc.name"
          ></iframe>
          <div class="docs__empty" v-else>
            <v-icon name="fileLines" :size="60" />
            <span>
              Предпросмотр файлов {{ currentDoc.extension.toUpperCase() }} недоступен в браузере.<br />
              Скачайте документ, чтобы открыть его.
            </span>
            <a
              class="docs__download-btn"
              :href="currentDoc.url"
              :download="fileName(currentDoc)"
              target="_blank"
              rel="noopener"
              @click="markViewed(currentDoc)"
            >
              <i class="el-icon-download"></i> Скачать {{ fileName(currentDoc) }}
            </a>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "Docs",
  data() {
    return {
      currentDoc: null,
      loading: true,
    };
  },
  computed: {
    ...mapState("docs", { docs: "list" }),
    mobileMode() {
      return this.$store.state.mobileMode;
    },
  },
  methods: {
    ...mapActions("docs", ["fetchDocs", "markViewed"]),
    isPdf(doc) {
      return doc.extension === "pdf";
    },
    fileName(doc) {
      return `${doc.name}.${doc.extension}`;
    },
    selectDoc(doc) {
      // На мобильных встроенный просмотр PDF работает плохо — открываем файл напрямую
      if (this.mobileMode) {
        this.openFile(doc);
        return;
      }

      this.currentDoc = doc;

      if (this.isPdf(doc)) {
        this.markViewed(doc);
      }
    },
    openFile(doc) {
      const link = document.createElement("a");
      link.href = doc.url;
      link.target = "_blank";
      link.rel = "noopener";

      if (!this.isPdf(doc)) {
        link.download = this.fileName(doc);
      }

      link.click();
      this.markViewed(doc);
    },
  },
  async mounted() {
    await this.fetchDocs();
    this.loading = false;
  },
};
</script>

<style lang="scss">
.docs {
  display: flex;
  flex-direction: column;
  height: 100%;
  .docs__title {
    width: 100%;
  }
  .docs__layout {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 20px;
    flex-grow: 1;
    min-height: 0;
  }
  .docs__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .docs__item {
    width: 100%;
    display: grid;
    grid-template-columns: 50px 1fr max-content;
    align-items: center;
    column-gap: 10px;
    padding: 12px;
    text-align: left;
    font-size: 15px;
    color: $primary-text-clr;
    background-color: $basic-white;
    border: 1px solid $light-border-clr;
    border-radius: 10px;
    cursor: pointer;
    transition-duration: 0.3s;
    transition-property: border-color, background-color;
    &:hover {
      border-color: $brand-clr-1;
    }
  }
  .docs__item--active {
    border-color: $brand-clr-1;
    background-color: $extra-light-border;
  }
  .docs__type {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 28px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    color: $basic-white;
    background-color: $info-clr;
  }
  .docs__type--pdf {
    background-color: #d93831;
  }
  .docs__type--docx {
    background-color: #2b579a;
  }
  .docs__type--pptx {
    background-color: #c4502b;
  }
  .docs__preview {
    display: flex;
    flex-direction: column;
    min-height: 70vh;
    border: 1px solid $light-border-clr;
    border-radius: 10px;
    overflow: hidden;
  }
  .docs__preview-header {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 15px;
    border-bottom: 1px solid $light-border-clr;
  }
  .docs__preview-title {
    flex-grow: 1;
    font-weight: bold;
    color: $regular-text-clr;
  }
  .docs__action {
    color: $brand-clr-1;
    white-space: nowrap;
    &:hover {
      color: lighten($brand-clr-1, 10%);
    }
  }
  .docs__frame {
    flex-grow: 1;
    width: 100%;
    border: none;
  }
  .docs__empty {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 15px;
    padding: 20px;
    text-align: center;
    color: $secondary-text-clr;
  }
  .docs__download-btn {
    padding: 10px 20px;
    border-radius: 6px;
    color: $basic-white;
    background-color: $brand-clr-1;
    &:hover {
      background-color: lighten($brand-clr-1, 10%);
    }
  }
}
@include breakPoints(lg) {
  .docs .docs__layout {
    grid-template-columns: 1fr;
  }
}
</style>
